import { ReactNode, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgAuth } from '../contexts/OrgAuthContext'
import UserMenu from './UserMenu'
import LanguageSwitcher from './LanguageSwitcher'
import NotificationsBell from './notifications/NotificationsBell'
import NavLauncher from './NavLauncher'
import { Z } from '../lib/zIndex'

export interface BreadcrumbItem {
  label: string
  to?: string
}

interface AppHeaderProps {
  title: ReactNode
  titleTabs?: ReactNode
  leftExtra?: ReactNode
  rightContent?: ReactNode
  /** When provided, replaces the plain title in the center panel with a breadcrumb (desktop only). */
  breadcrumb?: BreadcrumbItem[]
}

export default function AppHeader({ title, titleTabs, leftExtra, rightContent, breadcrumb }: AppHeaderProps) {
  const { session, isLoading } = useOrgAuth()
  const { t } = useTranslation()

  return (
    <header className={`bg-white border-b border-slate-200 sticky top-0 z-[${Z.header}] pt-safe`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-stretch">
          {/* Left — module launcher + optional extra */}
          <div className="flex items-center gap-3">
            <NavLauncher />
            {leftExtra && (
              <>
                <div className="h-6 w-px bg-slate-200" />
                <div className="flex items-center">{leftExtra}</div>
              </>
            )}
          </div>

          {/* Center — title / breadcrumb with full-height tinted background */}
          <div className="hidden sm:flex items-center gap-3 px-6 border-x border-transparent">
            {breadcrumb ? (
              <nav aria-label="breadcrumb" className="flex items-center gap-1.5">
                {breadcrumb.map((item, i) => (
                  <Fragment key={i}>
                    {i > 0 && (
                      <svg className="w-3 h-3 text-slate-300 shrink-0 rtl:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                    {item.to
                      ? <Link to={item.to} className="text-xs text-slate-500 hover:text-slate-600 transition-colors whitespace-nowrap">{item.label}</Link>
                      : <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">{item.label}</span>
                    }
                  </Fragment>
                ))}
              </nav>
            ) : (
              typeof title === 'string'
                ? <span className="text-sm font-semibold text-slate-800">{title}</span>
                : <div className="flex items-center">{title}</div>
            )}
            {titleTabs ? <div className="hidden md:flex items-center">{titleTabs}</div> : null}
          </div>

          {/* Right — actions */}
          <div className="flex items-center justify-end gap-4">
            {rightContent}

            {/* Notifications */}
            <NotificationsBell />

            <div className="hidden sm:block"><LanguageSwitcher /></div>
            {session ? (
              <UserMenu />
            ) : (
              <span className="text-sm text-slate-500">
                {isLoading ? t('common.loadingSession', 'Loading session…') : t('common.notSignedIn', 'Not signed in')}
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
